import React from "react";
import values from "lodash/values";
import pick from "lodash/pickBy";
import moment from "moment";
import { RaisedButton } from "material-ui";
import { Table, Tr, Td, Sort } from "reactable";

import FilmTable from "./partials/film-table.jsx";
import CloneFilmWidget from "./partials/clone-film-widget.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import FilmActions from "../actions/FilmActions";
import FilmStore from "../stores/FilmStore";
import FilmUtil from "../utils/FilmUtil";
import EventActions from "../actions/EventActions";
import EventStore from "../stores/EventStore";
import TagActions from "../actions/TagActions";

import config from "../config";
import UserStore from "../stores/UserStore";

import WorkingIndicator from "./partials/working-indicator.jsx";
import ImportFilmsButton from "./partials/import-films-button.jsx";

class Films extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showCloneFilmWidget: false,

      activeFilms: [],
      inactiveFilms: []
    };

    this.onBulkImportFromFile = this.onBulkImportFromFile.bind(this);
    this.onShowCloneWidget = this.onShowCloneWidget.bind(this);
    this.onCloseCloneWidget = this.onCloseCloneWidget.bind(this);
    this.onCloneFilms = this.onCloneFilms.bind(this);
  }

  static getStores() {
    return [FilmStore, EventStore];
  }

  static getPropsFromStores(props) {
    let bucket = props.params.eventBucketId,
      bucketFilms = FilmStore.getFilmsForEventBucket(bucket),
      bucketEvents = EventStore.getEventsForEventBucket(bucket);

    let films = values(
      pick(bucketFilms, e => {
        return e.event_bucket === bucket && e.type !== "livestream";
      })
    );

    return {
      films,
      events: bucketEvents,
      getAllUserFilms: FilmStore.getAllUserFilms
    };
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  getStyles() {
    return {};
  }

  downloadCsv() {
    window.open(
      `${config.endpoint}event_buckets/${
        this.props.params.eventBucketId
      }/films.csv?${UserStore.makeApiKeyQueryString()}`,
      "_blank"
    );
  }

  prepareFilms(props) {
    const { films, events } = props;
    if (!films || !events) {
      return;
    }

    // Separate active films from inactive films based on inclusion in active events
    let activeFilms = [],
      inactiveFilms = [];

    // Mark active events
    const now = moment();
    let activeEvents = events.map(currentEvent => {
      const endTime = moment(currentEvent.end_time);
      if (
        endTime.isAfter(now) ||
        !currentEvent.is_dated ||
        currentEvent.end_time === undefined
      ) {
        return true;
      }
      return false;
    });

    // Iterate the films
    for (let f = 0; f < films.length; f++) {
      const currentFilm = films[f];
      let filmIsActive = false;
      let filmIsScheduled = false;

      // Iterate all events (active and inactive)
      for (let e = 0; e < events.length; e++) {
        const currentEvent = events[e];
        let activeEvent = activeEvents[e]; // boolean

        // Iterate all films in this event.  If the film is in any
        // event, it is scheduled.  It it is in a futureEvent, it is active
        for (let ef = 0; ef < currentEvent.films.length; ef++) {
          const filmInEvent = currentEvent.films[ef];
          if (currentFilm.id === filmInEvent.id) {
            filmIsScheduled = true;
            if (activeEvent) {
              filmIsActive = true;
            }
            break;
          }
        }

        // if the film is active in this event, we can break, no need
        // to keep iterating for this film. If it is scheduled but not
        // active, we need to keep checking to make sure it is not
        // active in another event
        if (filmIsActive) {
          break;
        }
      }

      // separate into active/inactive arrays
      if (filmIsActive || !filmIsScheduled) {
        activeFilms.push(currentFilm);
      } else {
        inactiveFilms.push(currentFilm);
      }
    }

    this.setState({
      activeFilms,
      inactiveFilms
    });
  }

  componentWillMount() {
    EventActions.fetchAllForBucket(this.props.params.eventBucketId, false);
    FilmActions.fetchAllForEventBucket(this.props.params.eventBucketId);
    FilmActions.fetchAllForUser();
  }

  componentDidMount() {
    this.prepareFilms(this.props);
  }

  componentWillReceiveProps(newProps) {
    const _normalize = arr =>
      arr
        .map(f => f.id)
        .sort()
        .join(",");

    if (
      _normalize(newProps.films) !== _normalize(this.props.films) ||
      _normalize(newProps.events) !== _normalize(this.props.events)
    ) {
      this.prepareFilms(newProps);
    }
  }

  onBulkImportFromFile() {
    this.context.router.push(
      `/event_buckets/${this.props.params.eventBucketId}/bulk_import_films`
    );
  }

  onShowCloneWidget() {
    this.setState({ showCloneFilmWidget: true });
  }

  onCloseCloneWidget() {
    this.setState({ showCloneFilmWidget: false });
  }

  onCloneFilms(cloneList) {
    const clone = async () => {
      let filmList = this.props.getAllUserFilms();
      for (const e of cloneList) {
        // Clone one-by-one to prevent clone_tags accidentally making multiple copies
        let thisFilm = filmList.find(f => f.id === e);
        if (thisFilm) {
          const newFilm = {
            type: thisFilm.type,
            name: thisFilm.name,
            short_description: thisFilm.short_description,
            description: thisFilm.description,
            trailer_url: thisFilm.trailer_url,
            still_image_external: thisFilm.still_image,
            cover_image_external: thisFilm.cover_image,
            poster_image_external: thisFilm.poster_image,
            clone_tags: true,
            tags: thisFilm.tags,
            details: thisFilm.details,
            credits: thisFilm.credits,
            visibility: thisFilm.visibility,
            unballoted: thisFilm.unballoted,
            virtual_content: thisFilm.virtual_content
              ? thisFilm.virtual_content.id
              : null,
            event_bucket: this.props.params.eventBucketId
          };

          const createdFilm = await FilmUtil.create(
            UserStore.getState().apiKey,
            newFilm
          );
          FilmActions.updateFilm(createdFilm.id, createdFilm);
        }
      }
      TagActions.fetchAllForBucket(this.props.params.eventBucketId);
      FilmActions.fetchAllForEventBucket(this.props.params.eventBucketId);
      this.setState({ showCloneFilmWidget: false });
    };
    clone();
  }

  render() {
    let styles = this.getStyles(),
      bucket = this.props.params.eventBucketId,
      { films, events } = this.props,
      allFilms = this.props.getAllUserFilms();

    if (events.length === 0 && EventStore.getState().isFetching) {
      return <WorkingIndicator fullPage={true} />;
    }
    if (films.length === 0 && FilmStore.getState().isFetching) {
      return <WorkingIndicator fullPage={true} />;
    }

    let { activeFilms, inactiveFilms } = this.state;

    return (
      <div>
        <div>
          <FilmTable
            eventBucket={bucket}
            films={activeFilms}
            createButton={
              <span>
                {UserStore.hasPermission("edit_film") ? (
                  <RaisedButton
                    label="Create new film"
                    primary={true}
                    onTouchTap={() =>
                      this.context.router.push(
                        `/event_buckets/${this.props.params.eventBucketId}/films/new`
                      )
                    }
                  />
                ) : null}
                {UserStore.hasPermission("bulk_import_films") ? (
                  <ImportFilmsButton
                    style={{ marginLeft: 7, verticalAlign: "top" }}
                    label="Import films..."
                    eventBucketId={bucket}
                    onBulkImportClick={this.onBulkImportFromFile}
                    onCloneFilmsClick={this.onShowCloneWidget}
                  />
                ) : null}
                <RaisedButton
                  style={{
                    marginLeft: UserStore.hasPermission("view_film_list")
                      ? 7
                      : 0
                  }}
                  onTouchTap={() => this.downloadCsv()}
                  label="Download all (.csv)"
                />
              </span>
            }
          />
          {this.state.showCloneFilmWidget ? (
            <CloneFilmWidget
              films={allFilms}
              currentBucket={bucket}
              onClose={this.onCloseCloneWidget}
              onCloneFilms={this.onCloneFilms}
            />
          ) : null}
        </div>
        {inactiveFilms.length ? (
          <div>
            <h2 style={{ textAlign: "center", marginTop: "1.5em" }}>
              Inactive films
            </h2>
            <FilmTable eventBucket={bucket} films={inactiveFilms} />
          </div>
        ) : null}
      </div>
    );
  }
}

export default connectToStores(Films);



// WEBPACK FOOTER //
// ./src/app/components/films.jsx